import { ApolloError } from '@apollo/client';

import {
  VariantFragmentFragment,
  GetProductViewQuery,
} from '@notino/shared/definitions/types';
import {
  AdditionalServicesAvailability,
  LoyaltyDiscount,
} from '@notino/web-tracking';

import { IEngraving } from './../../ProductListing/model';
import { IModifaceEffectVariants } from './ModiFaceModal/model';

export const getModifaceEffectsForVariant = (
  modifaceMakeUpData: GetProductViewQuery['productDetailByMasterId'][number]['variants'][number]['modifaceMakeUpData']
) => {
  return modifaceMakeUpData.reduce((acc, effect) => {
    // Loop through shades
    const shades = effect.shades.map((shade) => ({
      ...shade,
      category: effect.category,
    }));
    return [...acc, ...shades];
  }, []);
};

export const getModifaceVariants = (
  variants: GetProductViewQuery['productDetailByMasterId'][number]['variants'][number][]
): IModifaceEffectVariants => {
  return variants.reduce((result, { id, modifaceMakeUpData }) => {
    if (!modifaceMakeUpData || modifaceMakeUpData.length === 0) {
      return result;
    }

    // If it has modiface effect
    return {
      ...result,
      [id]: getModifaceEffectsForVariant(modifaceMakeUpData),
    };
  }, <IModifaceEffectVariants>{});
};

export const getModifaceAvailability = (
  hasWebcam: boolean,
  hasModiface: boolean
): AdditionalServicesAvailability => {
  if (!hasModiface) {
    return AdditionalServicesAvailability.notAvailable;
  }

  return hasWebcam
    ? AdditionalServicesAvailability.available
    : AdditionalServicesAvailability.hidden;
};

export const getTryItFirstAvailability = (
  cartTryItFirstShow: boolean,
  tryItFirstCampaigns: GetProductViewQuery['productDetailByMasterId'][number]['campaigns'],
  selectedVariant: VariantFragmentFragment,
  additionalServicesError: ApolloError
) => {
  if (additionalServicesError) {
    return AdditionalServicesAvailability.timeout;
  }

  if (!cartTryItFirstShow) {
    return AdditionalServicesAvailability.hidden;
  }

  if (
    tryItFirstCampaigns.some(
      (tryItFirstCampaign) =>
        tryItFirstCampaign.productId === selectedVariant.id
    )
  ) {
    return AdditionalServicesAvailability.available;
  }

  return AdditionalServicesAvailability.notAvailable;
};

export const getEngravingAvailability = (
  cartEngravingShow: boolean,
  engraving: IEngraving,
  additionalServicesError: ApolloError
): AdditionalServicesAvailability => {
  if (additionalServicesError) {
    return AdditionalServicesAvailability.timeout;
  }

  if (engraving.allowed) {
    return cartEngravingShow && engraving.config.isAvailable
      ? AdditionalServicesAvailability.available
      : AdditionalServicesAvailability.hidden;
  }

  return AdditionalServicesAvailability.notAvailable;
};

export const getVariantDescription = (variant, product): string =>
  variant.attributes && variant.attributes.Master
    ? product.annotation
    : variant.variantName;

export const getLoyaltyDiscountState = (
  customerBenefits: GetProductViewQuery['productDetailByMasterId'][number]['variants'][number]['customerBenefits']
) => {
  if (customerBenefits?.availableDiscount) {
    return LoyaltyDiscount.available;
  }

  if (customerBenefits?.customerDiscount) {
    return LoyaltyDiscount.active;
  }

  return LoyaltyDiscount.notAvailable;
};

export const isGiftAvailable = (
  campaigns: GetProductViewQuery['productDetailByMasterId'][number]['campaigns']
) => {
  return Boolean(
    campaigns.find(
      (campaign) =>
        campaign.displayOnDetailPage &&
        (campaign.detailPageText || campaign.text)
    )
  );
};

export const getModifaceHairEffectsForVariant = (
  hairModifaceEffects: GetProductViewQuery['productDetailByMasterId'][number]['variants'][number]['hairModifaceEffects']
) => {
  return hairModifaceEffects.reduce((acc, effect) => {
    // Loop through shades
    const shades = effect.shades.map((shade) => ({
      ...shade,
      category: effect.category,
      histogram: {
        r: shade.histogram_r,
        g: shade.histogram_g,
        b: shade.histogram_b,
        c: shade.histogram_c,
        totalpixels: shade.totalpixels,
      },
    }));
    return [...acc, ...shades];
  }, []);
};

export const getModifaceHairVariants = (
  variants: GetProductViewQuery['productDetailByMasterId'][number]['variants'][number][]
): IModifaceEffectVariants => {
  return variants.reduce((result, { id, hairModifaceEffects }) => {
    if (!hairModifaceEffects || hairModifaceEffects.length === 0) {
      return result;
    }
    return {
      ...result,
      [id]: getModifaceHairEffectsForVariant(hairModifaceEffects),
    };
  }, {} as IModifaceEffectVariants);
};
